import * as React from 'react'
import { WebAdminUser } from '../api'
import { LicensedAgency } from './CurrentCustomerContext'

export interface AuthState {
  user: WebAdminUser | null
  loginSessionTimeoutInMinutes: number
  lastApiCall: number | null
  authorizedAgencies: LicensedAgency[]
  agencies: LicensedAgency[]
}

type LoginAction = {
  type: 'SetSession'
  payload: AuthState
}

type UpdateEmailAddressAction = {
  type: 'UpdateEmailAddress'
  payload: string
}

type LogoutAction = {
  type: 'Logout'
}

type ApiActivity = {
  type: 'ApiActivity'
  payload: number
}

type AuthorizedAgenciesAction = {
  type: 'AuthorizedAgenciesAction'
  payload: LicensedAgency[]
}

type AgenciesAction = {
  type: 'AgenciesAction'
  payload: LicensedAgency[]
}

export type AuthAction =
  | LoginAction
  | LogoutAction
  | ApiActivity
  | UpdateEmailAddressAction
  | AuthorizedAgenciesAction
  | AgenciesAction

export interface AuthContextState {
  state: AuthState
  dispatch: (action: AuthAction) => void
}

// we toss the session info into a SessionStorage item so that if the user refreshes their page we know what the info was without having to hit the server
export const SESSION_KEY = 'FieldOps::WebAdmin::Session'

const getDefaultAuthStatus = () => {
  if (!sessionStorage.getItem(SESSION_KEY)) return null

  return JSON.parse(
    sessionStorage.getItem(SESSION_KEY) as string,
  ) as WebAdminUser
}

const DEFAULT_AUTH_STATUS: AuthContextState = {
  state: {
    user: getDefaultAuthStatus(),
    loginSessionTimeoutInMinutes: 10,
    lastApiCall: null,
    authorizedAgencies: [],
    agencies: []
  },
  dispatch: (action: AuthAction) => {},
}

const LOGGED_OUT_STATUS: AuthContextState = {
  state: {
    user: null,
    loginSessionTimeoutInMinutes: 10,
    lastApiCall: null,
    authorizedAgencies: [],
    agencies: []
  },
  dispatch: (action: AuthAction) => {},
}

const reducer = (state: AuthState, action: AuthAction) => {
  switch (action.type) {
    case 'ApiActivity': {
      return {
        ...state,
        lastApiCall: action.payload,
      }
    }
    case 'SetSession':
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(action.payload))

      return {
        ...state,
        user: action.payload.user,
        loginSessionTimeoutInMinutes:
          action.payload.loginSessionTimeoutInMinutes,
        lastApiCall: action.payload.lastApiCall,
      }
    case 'UpdateEmailAddress':
      const updatedUser: WebAdminUser = {
        ...state.user!,
        email: action.payload,
      }
      const updatedSession: AuthState = {
        ...state,
        user: updatedUser,
      }
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession))

      return {
        ...updatedSession,
      }
    case 'Logout':
      sessionStorage.clear()

      return LOGGED_OUT_STATUS.state
    case 'AuthorizedAgenciesAction':
      const updatedAuthSession: AuthState = {
        ...state,
        authorizedAgencies: action.payload,
      }
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedAuthSession))

      return {
        ...updatedAuthSession,
      }
      case 'AgenciesAction':
        const updatedAgenciesAuthSession: AuthState = {
          ...state,
          agencies: action.payload,
        }
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedAgenciesAuthSession))
  
        return {
          ...updatedAgenciesAuthSession,
        }
      default:
      return state
  }
}

export const getInitialAuthStatus = () => DEFAULT_AUTH_STATUS.state

export const AuthContext = React.createContext<AuthContextState>(
  DEFAULT_AUTH_STATUS,
)

type AuthContextProviderProps = {
  children: any
}

export const AuthContextProvider: React.FunctionComponent<AuthContextProviderProps> = ({
  children,
}) => {
  const initialAuthState = getInitialAuthStatus()
  const [state, dispatch] = React.useReducer(reducer, initialAuthState)
  const value = { state, dispatch }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const AuthContextConsumer = AuthContext.Consumer
